import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Receipt, Download, Ban } from "lucide-react";
import api, { downloadBlob, errMsg } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Pagination, Money } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { formatTanggalWaktu, PAYMENT_TYPE } from "@/lib/format";
import { toast } from "sonner";

export default function Pembayaran() {
  const { projectId, projectParam } = useProjects();
  const [method, setMethod] = useState("all");
  const [type, setType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["payments", projectId, method, type, from, to, q, amountMin, amountMax, page],
    queryFn: async () => (await api.get("/payments", { params: {
      ...projectParam, method: method === "all" ? undefined : method, type: type === "all" ? undefined : type,
      date_from: from || undefined, date_to: to || undefined,
      q: q || undefined, amount_min: amountMin || undefined, amount_max: amountMax || undefined,
      page, page_size: 15,
    } })).data,
  });

  const openReceipt = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Struk tidak tersedia."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/payments/export.csv", {
        params: { ...projectParam, method: method === "all" ? undefined : method, type: type === "all" ? undefined : type, date_from: from || undefined, date_to: to || undefined, q: q || undefined, amount_min: amountMin || undefined, amount_max: amountMax || undefined },
        responseType: "blob",
      });
      downloadBlob(res.data, "pembayaran.csv");
    } catch { toast.error("Gagal mengekspor CSV."); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/payments/export.xlsx", {
        params: { ...projectParam, method: method === "all" ? undefined : method, type: type === "all" ? undefined : type, date_from: from || undefined, date_to: to || undefined, q: q || undefined, amount_min: amountMin || undefined, amount_max: amountMax || undefined },
        responseType: "blob",
      });
      downloadBlob(res.data, "pembayaran.xlsx");
    } catch { toast.error("Gagal mengekspor Excel."); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) { toast.error("Alasan wajib diisi."); return; }
    setCancelLoading(true);
    try {
      await api.post("/approvals", { type: "payment_cancel", target_type: "payment", target_id: cancelTarget.id, reason: cancelReason, proposed: { status: "cancelled" } });
      toast.success("Pengajuan pembatalan dikirim ke superadmin.", { description: "Bila disetujui, status sewa & kamar dikembalikan otomatis." });
      setCancelTarget(null); setCancelReason("");
    } catch (e) { toast.error(errMsg(e)); } finally { setCancelLoading(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Pembayaran" description="Catatan pembayaran DP dan pelunasan beserta struk PDF."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="payments-export-csv"><Download className="mr-2 h-4 w-4" /> Ekspor CSV</Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="payments-export-xlsx"><Download className="mr-2 h-4 w-4" /> Ekspor Excel</Button>
        </div>} />

      <SectionCard className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Jenis</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="payment-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Jenis</SelectItem>
                <SelectItem value="dp">DP Booking</SelectItem>
                <SelectItem value="settlement">Pelunasan</SelectItem>
                <SelectItem value="direct_settlement">Sewa Langsung (Tanpa DP)</SelectItem>
                <SelectItem value="extension_settlement">Pelunasan Perpanjangan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Metode</Label>
            <Select value={method} onValueChange={(v) => { setMethod(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="payment-filter-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Metode</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="cash">Tunai</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Dari</Label><Input type="date" className="h-9" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="payment-date-from" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" className="h-9" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="payment-date-to" /></div>
          <div><Label className="text-xs">Cari Penghuni</Label><Input className="h-9 w-[170px]" placeholder="Nama penghuni" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="payment-search" /></div>
          <div><Label className="text-xs">Nominal Min</Label><Input type="number" className="h-9 w-[130px]" placeholder="0" value={amountMin} onChange={(e) => { setAmountMin(e.target.value); setPage(1); }} data-testid="payment-amount-min" /></div>
          <div><Label className="text-xs">Nominal Maks</Label><Input type="number" className="h-9 w-[130px]" placeholder="∞" value={amountMax} onChange={(e) => { setAmountMax(e.target.value); setPage(1); }} data-testid="payment-amount-max" /></div>
        </div>
      </SectionCard>

      <SectionCard className="!p-0 overflow-hidden">
        {isLoading ? <Loading /> : !data?.items?.length ? (
          <EmptyState title="Belum ada pembayaran" icon={Receipt} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal Diterima</TableHead>
                    <TableHead>Jenis</TableHead>
                    <TableHead>Penghuni</TableHead>
                    <TableHead>Rumah / Kamar</TableHead>
                    <TableHead className="text-right">Nominal</TableHead>
                    <TableHead>Metode</TableHead>
                    <TableHead>Pencatat</TableHead>
                    <TableHead className="text-right">Struk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((p) => (
                    <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                      <TableCell className="whitespace-nowrap text-xs">{formatTanggalWaktu(p.received_at)}</TableCell>
                      <TableCell className="text-xs">{PAYMENT_TYPE[p.type] || p.type}</TableCell>
                      <TableCell className="font-medium">{p.resident_name || "-"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.house_name} · {p.room_number}</TableCell>
                      <TableCell className="text-right font-semibold"><Money value={p.amount} /></TableCell>
                      <TableCell className="text-xs">{p.method_label}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.recorded_by}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openReceipt(p)} data-testid={`download-pdf-receipt-button-${p.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {(p.type === "settlement" || p.type === "extension_settlement" || p.type === "direct_settlement") && p.status === "active" && (
                            <Button size="sm" variant="ghost" className="text-destructive" title="Ajukan pembatalan" onClick={() => setCancelTarget(p)} data-testid={`cancel-payment-button-${p.id}`}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="p-4"><Pagination page={page} total={data.total} pageSize={data.page_size} onPage={setPage} /></div>
          </>
        )}
      </SectionCard>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-payment-dialog">
          <DialogHeader><DialogTitle>Ajukan Pembatalan Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Pengajuan dikirim ke superadmin. Bila disetujui: pengakuan keuangan dibalik dan status sewa & kamar dikembalikan sepenuhnya.</p>
            {cancelTarget && <div className="rounded-lg bg-muted/40 p-3 text-sm">{PAYMENT_TYPE[cancelTarget.type]} · {cancelTarget.resident_name || "-"} · <Money value={cancelTarget.amount} /></div>}
            <div><Label>Alasan</Label><Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-payment-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Batal</Button>
            <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={submitCancel} disabled={cancelLoading} data-testid="cancel-payment-submit">Ajukan Pembatalan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
